import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { Caption1, Caption2, paragraph1CommonStyles, paragraph2CommonStyles } from '../../Typo'
import DatePicker from '../DateInput/DatePicker'
import { SizeVariant } from '../utils'
import { up } from 'styled-breakpoints'
import { pxToRem, hexToRgba } from '../../../utils'
import OutsideClickHandler from 'react-outside-click-handler'

const InputContainer = props => {
  const {
    placeholder,
    type,
    required,
    disabled,
    label,
    id = `input-${label}`,
    helperText,
    onChange,
    value,
    error,
    name,
    onBlur,
    onFocus,
    suffix,
    readOnly,
    innerRef,
    defaultValue,
    maxLength,
    className,
    size = SizeVariant.REGULAR,
    allowFutureDates,
    startDate,
    inputStylingProps,
    options,
    onSelect,
    filter,
    inputMode
  } = props

  const [focused, setFocused] = useState(false)
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [focusedLabel, setFocusedLabel] = useState(value || defaultValue)
  const [showDropDown, setShowDropDown] = useState(false)

  const onValueChange = inputValue => {
    if (!isDate) {
      const userInput = inputValue.target.value
      let filteredSuggestions = options
      if (filter && options) {
        filteredSuggestions = options.filter(option => option.toLowerCase().includes(userInput.toLowerCase()))
        if (value && value.length > 0 && filteredSuggestions && filteredSuggestions.length === 0) {
          setFilteredOptions(filteredSuggestions)
        } else {
          setFilteredOptions(filteredSuggestions)
        }
      }
    }
    onChange(inputValue)
  }
  const isControlled = !!(innerRef && onValueChange)

  const onOptionSelect = value => {
    onSelect(value)
    setFilteredOptions([])
  }

  const onInputFocus = event => {
    setFocused(true)
    setFocusedLabel(true)
    onFocus(event)
    setShowDropDown(true)
  }

  const onInputBlur = event => {
    if (!innerRef?.current?.value) {
      setFocusedLabel(false)
    }
    setFocused(false)
    onBlur(event)
  }

  const controlledProps = {
    onChange: onValueChange,
    value
  }

  const unControlledProps = {
    defaultValue
  }

  const inputProps = {
    id,
    'aria-label': label,
    placeholder: placeholder !== '' ? placeholder : '',
    type,
    error: error,
    ref: innerRef,
    focused,
    suffix,
    name,
    required,
    disabled,
    readOnly,
    maxLength,
    onFocus: onInputFocus,
    onBlur: onInputBlur,
    ...(isControlled ? controlledProps : unControlledProps),
    allowFutureDates,
    starDate: startDate,
    className: className,
    inputStylingProps: inputStylingProps,
    inputMode: inputMode
  }
  const isDate = type === 'date'
  const isTextArea = type === 'area'

  const renderReadOnlyInput = () => <ReadOnlyInput>{value}</ReadOnlyInput>

  const renderInput = () => {
    if (isDate) return <DatePicker autoComplete="off" {...inputProps} />
    if (isTextArea) return <TextInputArea autoComplete="off" {...inputProps} />
    return <TextInput autoComplete="off" {...inputProps} />
  }

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        if (options) {
          setShowDropDown(false)
        }
      }}
    >
      <InputWrapper
        error={error}
        disabled={disabled}
        data-testid={`ui-${isDate ? 'date' : 'input'}`}
        className={className}
        size={size}
        inputStylingProps={inputStylingProps}
      >
        {label && (
          <InputLabel
            data-value={value}
            error={error}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            focusedLabel={props?.focusedLabel}
            focused={focused}
            htmlFor={`input-${label}`}
            id={`basic-label-${label}`}
            size={size}
            placeholder="DD/MM/YYYY"
            className={className}
            inputStylingProps={inputStylingProps}
          >
            {DisplayLabel(required, label, disabled)}
          </InputLabel>
        )}
        <TextInputWrap readOnly={readOnly}>
          <InputFieldWrapper>
            {readOnly ? renderReadOnlyInput() : renderInput()}
            {showDropDown && options && value.length > 0 && options.length > 0 && (
              <StyledOptionWrapper>
                {filteredOptions &&
                  filteredOptions.map((value, index) => (
                    <StyledOptions onClick={() => onOptionSelect(value)}>{value}</StyledOptions>
                  ))}
              </StyledOptionWrapper>
            )}
          </InputFieldWrapper>

          {suffix && <Caption1>{suffix}</Caption1>}
        </TextInputWrap>

        {helperText && error.length === 0 && !readOnly && <HelperText disabled={disabled}> {helperText}</HelperText>}
        {!focused && error.length > 0 && <ErrorText className="error-text">{error}</ErrorText>}
      </InputWrapper>
    </OutsideClickHandler>
  )
}

export default InputContainer

const commonStyle = css`
  width: 100%;
  ${paragraph1CommonStyles};
  background: transparent;
  color: ${props => props.inputStylingProps?.input?.inputTextColor || props.theme.palette.grey1};
  font-weight: bold;

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  border-radius: 0;

  ${props =>
    props.suffix &&
    css`
      padding-right: ${props => (props.type === 'number' ? `20%` : `15%`)};
    `};

  border: none;
  padding: 4px 0;

  border-bottom: ${props => `${getBorderwidth(props)} solid ${getBorderColor(props)}`};

  :hover {
    border-bottom: ${props => props.inputStylingProps?.border?.bottomBorderSize || '2px'} solid
      ${props => props.inputStylingProps?.border?.bottomBorderColor || props.theme.palette.grey1};
  }
  :focus {
    outline: none;
    border-bottom: ${props => props.inputStylingProps?.border?.bottomBorderSize || '2px'} solid
      ${props => props.inputStylingProps?.border?.bottomBorderColor || props.theme.palette.grey1};
  }
  :disabled {
    color: ${props => props.theme.palette.grey6};
    border-bottom: 1px solid ${props => props.theme.palette.grey5};
    -webkit-text-fill-color: ${props => props.theme.palette.grey6};
  }

  ::placeholder {
    font-style: ${props => props.inputStylingProps?.input?.placeholderStyle || 'italic'};
    color: ${props => props.inputStylingProps?.input?.placeholderColor || props.theme.palette.grey4};
    font-weight: normal;
  }
`

const TextInputArea = styled.textarea`
  ${commonStyle}
`
const TextInput = styled.input`
  ${commonStyle}
  padding-bottom: ${pxToRem(2)};
  margin-top: ${pxToRem(2)}; 
`

const DisplayLabel = (required, label, disabled) => {
  return (
    <>
      {label + ' '}
      {required && <Required disabled={disabled}>*</Required>}
    </>
  )
}

const Required = styled.span`
  color: ${props => (props.disabled ? props.theme.palette.grey6 : props.theme.palette.blue1)};
`

const InputWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  min-height: 48px;

  height: ${pxToRem(62)};
  ${up('md')} {
    height: ${pxToRem(74)};
  }
`

const ErrorText = styled(Caption2)`
  color: ${props => props.theme.palette.red1};
`

const getLabelColor = props => {
  const { error, disabled, focused, theme, value, inputStylingProps } = props
  if (disabled) {
    return theme.palette.grey6
  }
  if (error && !value && focused) {
    return theme.palette.grey3
  }
  if (error && !disabled && focused) {
    return theme.palette.grey3
  }
  if (error && !disabled && !focused) {
    return theme.palette.grey3
  } else if (!error && !disabled) {
    return theme.palette.grey3
  }
}

const InputLabel = styled.label`
  transition: transform 150ms ease-in;
  transform-origin: top left;
  position: absolute;
  top: 0;
  left: 0;
  display: block;

  color: ${props => getLabelColor(props)};
  ${props =>
    props.focusedLabel || props.value || props.defaultValue
      ? css`
          ${paragraph2CommonStyles};
        `
      : css`
          transform: translateY(20px);
          ${paragraph1CommonStyles};
        `};
  &.inputField-v2 {
    transition: none;
    transform-origin: none;
    transform: none;
    position: normal;
    font-size: 12px;
    color: ${props => props.theme.palette.grey3};
    margin-top: -2px;
    ${up('md')} {
      margin-bottom: 12px;
      font-size: 14px;
    }
  }
`

const getBorderwidth = ({ error, disabled, focused }) => {
  if (disabled) {
    return '1px'
  }
  if (error && !disabled && !focused) {
    return '2px'
  } else if (!error && !focused && !disabled) {
    return '1px'
  }
  return '1px'
}

const getBorderColor = props => {
  const { error, disabled, focused, theme, value, inputStylingProps } = props
  if (disabled) {
    return inputStylingProps?.border?.bottomBorderColor || theme.palette.grey5
  }
  if (!focused && !disabled && error) {
    return theme.palette.red1
  } else if (!error && !disabled && !focused && !value) {
    return inputStylingProps?.border?.bottomBorderColor || theme.palette.grey3
  } else if (!error && !disabled && !focused && value) {
    return inputStylingProps?.border?.bottomBorderColor || theme.palette.grey3
  }
  // return theme.palette.grey3
  return inputStylingProps?.border?.bottomBorderColor || theme.palette.grey3
}

const TextInputWrap = styled.div`
  position: relative;
  margin-top: 16px;

  & ~ label {
    transform: translate(6px);
  }

  & :focus-within ~ label {
    transform: translateY(0px);
    outline: none;
    font-size: 14px;
    line-height: 20px;
  }

  & span {
    position: ${props => (!props.readOnly ? `absolute` : `static`)};
    right: ${props => (props.type === 'number' ? `24px` : `0`)};
    margin-left: 4px;
  }
`

const HelperText = styled(Caption2)`
  color: ${props => props.theme.palette.grey6};
`

const ReadOnlyInput = styled.label`
  line-height: ${pxToRem(24)};
  color: ${props => props.theme.palette.grey1};
  margin-left: 2px;
  margin-right: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
  font-weight: bold;
`

const InputFieldWrapper = styled.div`
  position: relative;
`

const StyledOptionWrapper = styled.div`
  max-height: ${pxToRem(156)};
  height: auto;
  position: absolute;
  z-index: 1000;
  top: 10;
  right: 0;
  left: 0;
  overflow: auto;
  box-shadow: 0 ${pxToRem(2)} ${pxToRem(8)} ${props => hexToRgba(props.theme.palette.black, 0.16)};
  border-radius: 0 0 ${pxToRem(8)} ${pxToRem(8)};
  background-color: ${props => props.theme.palette.white};
`

const StyledOptions = styled.option`
  padding: ${pxToRem(8)};
  cursor: pointer;
  color: ${props => props.theme.palette.black3};
  :hover {
    background-color: ${props => props.theme.palette.grey5};
  }
  ${up('md')} {
    padding: ${pxToRem(12)} ${pxToRem(16)};
  }
`
